import { useEffect, useState } from "react";
import axios from "axios";
import { ExternalLink, Music2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FeaturedBands() {
  const [bands, setBands] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/featured-bands`);
        if (mounted) setBands(res.data || []);
      } catch (e) {
        /* silent */
      } finally {
        if (mounted) setLoaded(true);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  // Hide the whole section when nothing is featured yet — keeps the page clean
  if (loaded && bands.length === 0) return null;

  return (
    <section
      id="featured-bands"
      className="relative border-t border-zinc-800 bg-zinc-950 scanlines"
      data-testid="featured-bands-section"
    >
      <div className="px-4 md:px-10 py-12 md:py-20">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-3">
              //_FEATURED_THIS_WEEK
            </div>
            <h2 className="font-display text-5xl md:text-7xl leading-none text-zinc-50 glitch">
              BANDS WE'RE <span className="text-red-600">BLASTING</span>.
            </h2>
          </div>
          <div className="text-sm text-zinc-300 max-w-md">
            Hand-picked by our DJs. Independent, underrated, or just plain
            heavy — these are the artists Digital Revolution Radio is
            championing right now.
          </div>
        </div>

        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-0 border-l border-t border-zinc-800"
          data-testid="featured-bands-grid"
        >
          {bands.map((b) => (
            <BandCard key={b.id} band={b} />
          ))}
        </div>
      </div>
    </section>
  );
}

function BandCard({ band }) {
  return (
    <article
      className="border-r border-b border-zinc-800 bg-black flex flex-col group hover:border-red-600 transition-colors"
      data-testid={`featured-band-${band.id}`}
    >
      <div className="relative aspect-square overflow-hidden border-b border-zinc-800">
        <img
          src={band.photo_url}
          alt={band.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, transparent 0%, transparent 60%, rgba(9,9,11,0.85) 100%)",
          }}
        />
        <div className="absolute top-2 left-2 text-[9px] tracking-widest uppercase bg-red-600 text-black px-1.5 py-0.5 font-display">
          FEATURED
        </div>
      </div>
      <div className="p-3 flex flex-col flex-1 gap-1.5">
        <h3 className="font-display text-base md:text-lg leading-tight text-zinc-50 tracking-tight line-clamp-2">
          {band.name}
        </h3>
        {(band.featuring_dj || band.featuring_show) && (
          <div className="text-[9px] tracking-[0.25em] text-zinc-300 uppercase leading-tight">
            <span className="text-red-500">
              {band.featuring_dj || "DRR"}
            </span>
            {band.featuring_show && (
              <>
                {" · "}
                <span className="text-zinc-100">{band.featuring_show}</span>
              </>
            )}
          </div>
        )}
        <p className="text-xs text-zinc-200 leading-snug flex-1 whitespace-pre-wrap line-clamp-3">
          {band.blurb}
        </p>
        {band.music_link && (
          <a
            href={band.music_link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-center gap-1.5 self-start bg-red-600 hover:bg-white hover:text-black text-white font-display tracking-widest uppercase px-2.5 py-1.5 transition-colors border border-zinc-700 text-[10px]"
            data-testid={`featured-band-link-${band.id}`}
          >
            <Music2 className="w-3 h-3" /> Listen
            <ExternalLink className="w-2.5 h-2.5 opacity-70" />
          </a>
        )}
      </div>
    </article>
  );
}
